import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Permission, roleHasPermissions } from '@lizrd/contracts';
import { useAuthStore } from '../store/auth';
import { Alert } from '../components/ui/alert';
import { DomainAccessManager } from '../components/DomainAccessManager';
import { SystemSettings } from '../components/SystemSettings';
import { ReviewSettings } from '../components/ReviewSettings';
import { ComplianceSettings } from '../components/ComplianceSettings';
import { useFeature } from '../lib/useFeature';

/**
 * Domain-wide settings — manage who can access the domain and the domain's
 * system settings. Split out of the connected-sources page so "Integrations" and
 * "Settings" are distinct tabs. Both sections need DomainsManage (administrators
 * and managers); everyone else sees a read-only note.
 */
export function DomainSettingsPage() {
  const { domainId = '' } = useParams();
  const location = useLocation();
  const role = useAuthStore((s) => s.user?.role);
  // The sandbox is a read-only demo (also enforced 403 server-side).
  const isSandbox = useAuthStore((s) => s.user?.organizationKind === 'SANDBOX');
  const canManage =
    !isSandbox && (role ? roleHasPermissions(role, [Permission.DomainsManage]) : false);
  const complianceOn = useFeature('compliance');

  // Deep-link to a settings section (e.g. #manage-access from the Domains list, or
  // #reviews from the Reviews page's "Enable reviews" CTA). A short delay lets the
  // async sections above settle their height before we scroll.
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return;
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(t);
  }, [location.hash]);

  if (!canManage) {
    return (
      <Alert variant="info">
        Domain settings — access and system settings — are managed by administrators and managers.
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <DomainAccessManager domainId={domainId} />
      <div className="border-t border-hairline pt-6">
        <SystemSettings domainId={domainId} />
      </div>
      <div id="reviews" className="scroll-mt-4 border-t border-hairline pt-6">
        <ReviewSettings domainId={domainId} />
      </div>
      {complianceOn && (
        <div id="compliance" className="scroll-mt-4 border-t border-hairline pt-6">
          <ComplianceSettings domainId={domainId} />
        </div>
      )}
    </div>
  );
}
